/**
 * Is a notification still about something that exists?
 *
 * Reported from an Android handset: signing in produced a "sent you a Cue"
 * notification with an empty Cues tab behind it. The cause was server-side —
 * every push we sent omitted Expo's `ttl`, which means "each provider's own
 * default - 4 weeks", so FCM had been holding the announcement long after the
 * Cue expired. That is fixed where it belongs, in `push-ttl.ts`.
 *
 * This is the client's half, and it exists for two reasons the server fix
 * cannot cover:
 *
 *  - **What is already queued.** Every push sent before the deadline shipped is
 *    still sitting in Google's queue with four weeks on it. No server change
 *    recalls those.
 *  - **What ends early.** A Cue can be accepted, declined or withdrawn long
 *    before it expires. A deadline set at send time cannot know that, so a push
 *    can be inside its TTL and still be about nothing.
 *
 * The rule is the same one the server applies and it is not "old notifications
 * go away": an unread message stays worth reading for as long as it is unread.
 * A notification is stale only when the ACTIVITY behind it is gone.
 *
 * Pure on purpose — no expo-notifications import — so the decision is testable
 * under node, like `cue-cooldown-format.ts`.
 */

/** The subset of a notification's data payload this can judge. */
export type NotificationData = Record<string, unknown> | null | undefined;

/**
 * Has the activity this announces already finished?
 *
 * Judged only on `expiresAt`, which the server attaches to perishable pushes.
 * Anything without one is durable and never stale — that is the default, and
 * it is the safe direction: a message notification wrongly suppressed is one
 * the recipient never learns about.
 */
export function notificationExpired(
  data: NotificationData,
  now: Date = new Date(),
): boolean {
  const raw = data?.expiresAt;
  if (typeof raw !== "string") return false;
  const at = Date.parse(raw);
  // An unparseable date is not evidence of anything. Treat it as durable
  // rather than silently swallowing the notification.
  if (Number.isNaN(at)) return false;
  return at <= now.getTime();
}

/**
 * Which Cue this notification is about, if it is about one.
 *
 * Only the RECEIVED kind. `cue_sent` is the sender's own confirmation, and the
 * client that would reconcile it holds a different list; sweeping it here on
 * the recipient's pending inbox would dismiss it for the wrong reason.
 */
export function cueIdOf(data: NotificationData): string | null {
  if (data?.type !== "cue") return null;
  return typeof data.cueId === "string" ? data.cueId : null;
}
